import { useRef } from "react";
import { Move, ZoomIn, RotateCcw } from "lucide-react";

const KEYWORDS = {
  center: [50, 50], top: [50, 0], bottom: [50, 100], left: [0, 50], right: [100, 50],
  "top left": [0, 0], "top right": [100, 0], "bottom left": [0, 100], "bottom right": [100, 100],
};

const parsePos = (pos) => {
  if (!pos) return [50, 50];
  if (KEYWORDS[pos]) return KEYWORDS[pos];
  const m = String(pos).match(/(-?\d+(?:\.\d+)?)%\s+(-?\d+(?:\.\d+)?)%/);
  return m ? [parseFloat(m[1]), parseFloat(m[2])] : [50, 50];
};

const clamp = (n) => Math.min(100, Math.max(0, n));

// Interactive "move + zoom" picker. Non-destructive: stores a focal position
// ("x% y%") + a zoom scale that the display applies via CSS (WYSIWYG frame).
export const ImagePositioner = ({ image, position, zoom = 1, onChange, testid, aspectClass = "aspect-[16/7]", overlay = null, overlayText = null }) => {
  const [px, py] = parsePos(position);
  const frameRef = useRef(null);
  const drag = useRef(null);

  const emit = (x, y, z) => onChange({ position: `${Math.round(clamp(x))}% ${Math.round(clamp(y))}%`, zoom: z });

  const onDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const rect = frameRef.current.getBoundingClientRect();
    drag.current = { startX: e.clientX, startY: e.clientY, px, py, w: rect.width, h: rect.height };
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dxPct = ((e.clientX - d.startX) / d.w) * 100;
    const dyPct = ((e.clientY - d.startY) / d.h) * 100;
    emit(d.px - dxPct, d.py - dyPct, zoom);
  };
  const onUp = () => { drag.current = null; };

  if (!image) return null;

  return (
    <div data-testid={testid}>
      <div
        ref={frameRef}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        className={`relative w-full ${aspectClass} rounded-lg overflow-hidden border-2 border-[var(--border-gold)] cursor-move select-none bg-[var(--surface)] touch-none`}
        data-testid={testid ? `${testid}-frame` : undefined}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${image})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: `${px}% ${py}%`,
            transform: `scale(${zoom})`,
            transformOrigin: `${px}% ${py}%`,
          }}
        />
        {overlay != null && (
          <>
            <div className="absolute inset-0 pointer-events-none" style={{ background: `linear-gradient(to top, rgba(0,0,0,${Math.min(overlay + 0.3, 0.95)}), rgba(0,0,0,${overlay}) 45%, rgba(0,0,0,${Math.max(overlay - 0.2, 0.12)}))` }} />
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(0,0,0,0) 25%, rgba(0,0,0,0.5) 100%)" }} />
            {overlayText && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 pointer-events-none">
                <p className="font-script text-lg sm:text-2xl text-[#F8E9C8]">{overlayText.subtitle || "together forever"}</p>
                <h3 className="font-serif text-white text-2xl sm:text-4xl leading-none mt-1">{overlayText.names}</h3>
              </div>
            )}
          </>
        )}
        <div className="absolute inset-x-0 bottom-0 bg-black/45 text-white text-[11px] px-2 py-1 flex items-center gap-1 pointer-events-none">
          <Move className="w-3 h-3" /> Drag to move · this frame shows exactly what guests will see
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <ZoomIn className="w-4 h-4 text-[var(--gold)] shrink-0" />
        <input
          type="range"
          min="1"
          max="3"
          step="0.05"
          value={zoom}
          onChange={(e) => emit(px, py, parseFloat(e.target.value))}
          className="flex-1 accent-[var(--gold)]"
          data-testid={testid ? `${testid}-zoom` : undefined}
        />
        <span className="text-xs text-[var(--text-muted)] w-10 text-right">{Math.round(zoom * 100)}%</span>
        <button
          type="button"
          onClick={() => onChange({ position: "50% 50%", zoom: 1 })}
          className="text-xs text-[var(--gold-hover)] flex items-center gap-1 shrink-0"
          data-testid={testid ? `${testid}-reset` : undefined}
        >
          <RotateCcw className="w-3 h-3" /> Reset
        </button>
      </div>
    </div>
  );
};
